import { createContext, useContext, useEffect, useState, useCallback } from "react";

const TvModeContext = createContext(null);
const STORAGE_KEY = "reelvault_tv_mode";

// UA hints for TVs / Fire Stick Silk / game-console browsers.
const TV_UA = /SmartTV|Smart-TV|SMART-TV|Tizen|Web0S|WebOS|NetCast|AppleTV|Apple TV|GoogleTV|Google TV|Android ?TV|AFTV|BRAVIA|HbbTV|CrKey|AFT[A-Z]|Silk|VIERA|AQUOS|DTV|Roku|PlayStation|Nintendo|Xbox/i;
export const isTvUserAgent = () => typeof navigator !== "undefined" && TV_UA.test(navigator.userAgent || "");

function detectTv() {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (TV_UA.test(ua)) return true;
  // Very large, non-touch landscape screens (laptop→projector/TV) → default to TV UI.
  const big = Math.max(window.innerWidth, window.innerHeight) >= 1920;
  const coarseOrNoHover = window.matchMedia && window.matchMedia("(hover: none)").matches;
  return big && coarseOrNoHover;
}

export const TvModeProvider = ({ children }) => {
  const [tvMode, setTvMode] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved === "on") return true;
    if (saved === "off") return false;
    return detectTv();
  });

  useEffect(() => {
    const root = document.documentElement;
    if (tvMode) root.classList.add("tv-mode");
    else root.classList.remove("tv-mode");
    // .is-tv: real TV browsers (UA) or lean-back mode → focus-only UI, no hover, no overscroll bounce.
    root.classList.toggle("is-tv", tvMode || isTvUserAgent());
  }, [tvMode]);

  const toggleTvMode = useCallback(() => {
    setTvMode((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, next ? "on" : "off");
      return next;
    });
  }, []);

  return (
    <TvModeContext.Provider value={{ tvMode, setTvMode, toggleTvMode }}>
      {children}
    </TvModeContext.Provider>
  );
};

export const useTvMode = () => {
  const ctx = useContext(TvModeContext);
  if (!ctx) throw new Error("useTvMode must be used within TvModeProvider");
  return ctx;
};
